// hooks
import { Fragment, useEffect, useState, useCallback } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import useHttp from "hooks/use-http";

// routes
import { useNavigate } from "react-router-dom";

// material styles
import { Grid, Button, TextField, IconButton, Tooltip } from "@mui/material";

// material icons
import CheckIcon from "@mui/icons-material/Check";
import { Edit } from "@mui/icons-material";

// components
import LoadingCM from "components/shared/Loading";
import DrawerCM from "components/shared/Drawer";
import TableCM from "components/shared/Table";
import BackButton from "components/shared/BackButton";
import PaginationCM from "components/shared/Pagination";
import ReactHookFormSelect from "components/shared/ReactHookFormSelect";

// helper
import Toast from "helper/toast";
import GenerateHeadCell from "helper/generate-head-cell";

// styled component
import {
  StyledStack,
  StyledTitle,
  ButtonContainer,
  StyledMenuItem,
} from "./addSubcategory.style";

// request methods
import {
  addServiceSubCategoryRequest,
  getServiceSubCategoryRequest,
} from "api/requests/services";

// validation
import { serviceSubCategorySchema } from "validations";

// constanst
import { PAGINATION_LIMIT } from "constants";

// subcategory head cells
const headCells = GenerateHeadCell("services_subcategory_list");

// services categories...
const SERVICES_CATEGORIES = [
  { value: "consultation", title: "مشاوره" },
  { value: "performance", title: "اجرا" },
  { value: "supervision", title: "نظارت" },
  { value: "export-import-clearance", title: "صادرات واردات و ترخیص" },
];

export default function AddServicesSubCategory() {
  const navigate = useNavigate();

  const { request, loading, setLoading } = useHttp(false);

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(serviceSubCategorySchema),
  });

  // page state for pagination
  const [page, setPage] = useState(1);

  // subcategories state
  const [subCategoryData, setSubCategoryData] = useState({
    data: [],
    count: 0,
  });

  // get all categories data
  const getSubCategoriesData = useCallback(async () => {
    setLoading(true);
    const res = await request({
      url: getServiceSubCategoryRequest,
      values: {
        skip: (page - 1) * PAGINATION_LIMIT,
        limit: PAGINATION_LIMIT,
        search: null,
      },
      isMulty: true,
    });
    setLoading(false);
    if (res) {
      if (res.data.count < 1) {
        return setSubCategoryData({
          count: res.data.count,
          data: [{ content: "هیچ اطلاعاتی یافت نشد" }],
        });
      }
      let transformedData = res.data.data.map((items) => {
        // ctg = category
        const [{ value, title: formatedCategory }] = SERVICES_CATEGORIES.filter(
          (ctg) => ctg.value === items.category
        );

        return {
          id: items.id,
          title: items.title,
          category: formatedCategory,
          edit: (
            <IconButton
              color="primary"
              onClick={() => {
                navigate("/services/subcategory/edit/" + items.id);
              }}
            >
              <Tooltip title="ویرایش" placement="top">
                <Edit />
              </Tooltip>
            </IconButton>
          ),
        };
      });
      setSubCategoryData({
        data: transformedData,
        count: res.data.count,
      });
    }
  }, [page]);

  // add subcategory
  const onSubmit = async (data) => {
    const res = await request({
      url: () => addServiceSubCategoryRequest({ ...data }),
    });

    if (res) {
      reset({
        title: "",
      });
      Toast({ type: "success", title: "ثبت زیردسته " });
      getSubCategoriesData();
    }
    setLoading(false);
  };

  useEffect(() => {
    getSubCategoriesData();
  }, [getSubCategoriesData]);

  return (
    <DrawerCM title="افزودن زیردسته خدمات">
      {loading ? (
        <LoadingCM />
      ) : (
        <Fragment>
          <Grid container>
            <Grid item mt={3} xs={12}>
              <BackButton Route="/services" />
            </Grid>
            <StyledStack>
              <Grid>
                <StyledTitle>افزودن زیردسته</StyledTitle>
                <Grid container>
                  <Grid item md={5} xs={12} p={1.5}>
                    <TextField
                      label="عنوان زیردسته"
                      variant="outlined"
                      fullWidth
                      id="title"
                      {...register("title")}
                      error={Boolean(errors.title)}
                      helperText={errors.title?.message}
                    />
                  </Grid>
                  <Grid item md={5} xs={12} p={1.5}>
                    <ReactHookFormSelect
                      label="سردسته"
                      control={control}
                      title="سردسته"
                      fullWidth
                      id="category"
                      {...register("category")}
                      error={Boolean(errors.category)}
                      helperText={errors.category?.message}
                    >
                      {SERVICES_CATEGORIES.map((item, index) => (
                        <StyledMenuItem key={index} value={item.value}>
                          {item.title}
                        </StyledMenuItem>
                      ))}
                    </ReactHookFormSelect>
                  </Grid>
                  <ButtonContainer item md={2} xs={12}>
                    <Button
                      color="success"
                      startIcon={<CheckIcon />}
                      variant="contained"
                      sx={{ mt: 1, mr: 1 }}
                      onClick={handleSubmit(onSubmit)}
                    >
                      ثبت
                    </Button>
                  </ButtonContainer>
                </Grid>
              </Grid>
            </StyledStack>
          </Grid>

          <Grid container>
            <Grid item xs={12} my={2}>
              <TableCM
                title={"لیست زیردسته ها"}
                rows={subCategoryData.data}
                headCells={headCells}
                page={page}
                colspan={subCategoryData.count === 0 ? "11" : ""}
                fontSize="12px"
                cellFontSize="12px"
              />
            </Grid>
            {subCategoryData.count < 1 ? (
              ""
            ) : (
              <PaginationCM
                handleChange={(_, val) => setPage(val)}
                page={page}
                count={subCategoryData.count}
              />
            )}
          </Grid>
        </Fragment>
      )}
    </DrawerCM>
  );
}
